import {
  useRef,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useReducer,
} from "react";
import { Pagination, Table } from "rsuite";
import { ApiCaregiver } from "../../core/apis";
import { ApiSchemaCaregiver } from "../../models/api/caregiver";
import { useHistory } from "react-router-dom";
import { AppRouteUi } from "../../config/appRoute";
import { useAppSelector } from "../../core/redux";
import { useAppDispatch } from "../../core/redux";
import { PaginatedData } from "../../models/api/paginatedData";
import { ActionsCaregiverTable } from "../../core/redux/slices/sliceCaregiverTable";
import { TextCell } from "../../components/tableItem.component";
import { useAlertDialog } from "../../components/alertDialog";
import { AutoClockToggle } from "../../components/autoClockToggle.component";
import { GPSCheckInToggle } from "../../components/gpsCheckInToggle.component";
import { sortTableData } from "../../utils/tableFilter";
import { BooleanToggleFilterIcon } from "../../components/booleanToggleFilterIcon.component";
import { NotificationToggle, TableHeader } from "../../components";
import { convertToTwoDecimal } from "../../utils/convertToTwoDecimal";
import { FiEye, FiEdit, FiTrash2, FiPower } from 'react-icons/fi';

const { Column, HeaderCell, Cell } = Table;

type TableData = ApiSchemaCaregiver & {
  checked?: boolean;
  name?: string;
  availableFromDate?: Date;
  availableToDate?: Date;
};

type TypeTableKey = keyof TableData;

type CaregiverListProps = {
  isValidating?: boolean;
  data?: ApiSchemaCaregiver[];
  totalCount?: number;
  isInProfile?: boolean;
  dateRange?: {
    fromDate?: Date;
    toDate?: Date;
  };
  expirationDateFilter?: Date | null;
  certificationStatus?: string | null;
};

export const CaregiverList = ({
  data,
  isValidating,
  isInProfile,
  totalCount,
}: CaregiverListProps) => {
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [selected, setSelected] = useState<TableData[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const triggerRef = useRef<any>();
  const routerHistory = useHistory();
  const { alertDialogCtrl, alertDialogComponent } = useAlertDialog();
  const { pageNumber, pageSize, isLoading } = useAppSelector(
    (state) => state.caregiverTable
  );
  const dispatch = useAppDispatch();

  const [sortBy, setSortBy] = useState<TypeTableKey>("" as TypeTableKey);
  const [sortAsc, setSortAsc] = useState(true);
  const [filterGPSCheckIn, toggleFilterGPSCheckIn] = useReducer(
    (curr: number) => (curr + 1) % 3,
    2
  );
  const [filterAutoClockCheckIn, toggleFilterAutoClockCheckIn] = useReducer(
    (curr: number) => (curr + 1) % 3,
    2
  );

  useEffect(
    () =>
      setTableData(
        data?.map((caregiver) => {
          const { rate, ...rest } = caregiver;
          return {
            name: caregiver.firstName + " " + caregiver.lastName,
            patientName: caregiver.patientNames?.join(" , "),
            availableFromDate: new Date(caregiver.availableFrom),
            availableToDate: new Date(caregiver.availableTo),
            rate: convertToTwoDecimal(rate as unknown as number),
            ...rest,
          };
        }) ?? []
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  const CellRowSelection = useMemo((): ReactNode => {
    return (rowData: TableData) => (
      <input
        type="checkbox"
        checked={!!rowData.checked}
        onChange={(ev) =>
          rowData.sKey &&
          setTableData((tableData) =>
            tableData?.map((td) =>
              td.sKey === rowData.sKey
                ? {
                    ...td,
                    checked: ev.target.checked,
                  }
                : td
            )
          )
        }
      />
    );
  }, []);

  const selectAllRow = useCallback((checked: boolean) => {
    setTableData((tableData) =>
      tableData.map((td) => ({
        ...td,
        checked: checked,
      }))
    );
  }, []);

  const isAllChecked = useMemo(
    () => tableData.length > 0 && tableData.every((td) => !!td.checked),
    [tableData]
  );

  useEffect(() => {
    setSelected(tableData.filter((td) => !!td.checked));
  }, [tableData]);

  const updateDeleted =
    (sKeys: string[]) => async (res?: PaginatedData<ApiSchemaCaregiver>) => {
      let updated: PaginatedData<ApiSchemaCaregiver> = {
        totalCount: res?.totalCount || 0,
        list: res?.list.filter((i) => !sKeys.includes(i.sKey)) || [],
      };
      return updated;
    };

  const onDelete = useCallback(
    () =>
      alertDialogCtrl.open({
        message: "Are you sure you want to delete the selected caregivers?",
        proceedBtnText: "Delete",
        type: "danger",
        onSuccess: async () => {
          setIsDeleting(true);
          dispatch(ActionsCaregiverTable.setIsLoading(true));
          const selectedIds = selected.filter((s) => s.sKey).map((s) => s.sKey);
          await deleteItem(selectedIds);
          const sKeys = selected.map((item) => item.sKey);
          ApiCaregiver.GetAll.Mutate(updateDeleted(sKeys));
          setIsDeleting(false);
          dispatch(ActionsCaregiverTable.setIsLoading(false));
          dispatch(ActionsCaregiverTable.setRefetch(true));
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected]
  );

  const handleSelectMenu = (
    eventKey: string,
    event: Event,
    sKey?: string,
    rowData?: any,
    isDeActivated?: boolean
  ) => {
    if (eventKey === "delete" && sKey) {
      alertDialogCtrl.open({
        message: "Are you sure you want to delete this caregiver?",
        proceedBtnText: "Delete",
        type: "danger",
        onSuccess: async () => {
          dispatch(ActionsCaregiverTable.setIsLoading(true));
          const deleted = await deleteItem([sKey]);
          if (deleted) ApiCaregiver.GetAll.Mutate(updateDeleted([sKey]));
          dispatch(ActionsCaregiverTable.setIsLoading(false));
        },
      });
    } else if (eventKey === "changeStatus" && sKey) {
      alertDialogCtrl.open({
        message: `Are you sure you want to ${
          isDeActivated ? "Activate" : "Deactivate"
        } this Caregiver?`,
        body: !isDeActivated && (
          <p className="text-red-300">
            If you deactivate this caregiver, appointments associated with this
            caregiver won't work. Do you want to deactivate?
          </p>
        ),
        proceedBtnText: isDeActivated ? "Activate" : "Deactivate",
        type: "danger",
        onSuccess: async () => {
          dispatch(ActionsCaregiverTable.setIsLoading(true));
          await ApiCaregiver.ActivateDeactivate(sKey, !isDeActivated!);
          dispatch(ActionsCaregiverTable.setIsLoading(false));
        },
      });
    } else if (eventKey === "update" && sKey)
      routerHistory.push(AppRouteUi.Caregiver.UpdateById(sKey));
  };

  const deleteItem = async (sKey: string[]) => await ApiCaregiver.Delete(sKey);

  const filteredTableData = useMemo(() => {
    return sortTableData(tableData, sortBy, sortAsc);
  }, [tableData, sortBy, sortAsc]);

  return (
    <div>
      {alertDialogComponent}
      <TableHeader
        buttonText="Add New Caregiver"
        redirectPath={AppRouteUi.Caregiver.Add()}
        onDelete={onDelete}
        pageSize={pageSize}
        TableAction={ActionsCaregiverTable}
        isDeleting={isDeleting}
        isSelected={!!selected.length}
      />
      <Table
        autoHeight
        data={
          !totalCount
            ? filteredTableData.filter(
                (val, idx) =>
                  idx >= (pageNumber - 1) * pageSize &&
                  idx < pageNumber * pageSize
              )
            : filteredTableData
        }
        loading={isValidating || isLoading}
        onScroll={() => triggerRef.current?.close()}
        renderEmpty={() => (
          <div className="d-flex justify-content-center text-muted align-items-center h-100 text-center p-5">
            <h3 className="fw-light">No Caregiver Available</h3>
          </div>
        )}
        sortColumn={sortBy}
        sortType={sortAsc ? "asc" : "desc"}
        onSortColumn={(sortBy, sortAsc) => {
          setSortBy(sortBy as keyof TableData);
          setSortAsc(sortAsc === "asc");
        }}
        rowClassName="table-row-hover"
        bordered
        cellBordered
        hover
        classPrefix="rs-table"
        className="table-striped"
      >
        <Column align="center" flexGrow={0.2}>
          <HeaderCell className="fw-bold text-black">
            <input
              type="checkbox"
              checked={isAllChecked}
              onChange={(ev) => selectAllRow(ev.target.checked)}
            />
          </HeaderCell>
          <Cell>{CellRowSelection}</Cell>
        </Column>

        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Caregiver Name">Caregiver</span>
          </HeaderCell>
          <TextCell component="Caregiver" dataKey="name" />
        </Column>

        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Patient Name">Patient </span>
          </HeaderCell>
          <TextCell component="Caregiver" dataKey="patientName" />
        </Column>

        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">
            <span title="Phone Number">Phone</span>
          </HeaderCell>
          <TextCell component="Caregiver" dataKey="phoneNumber" />
        </Column>

        <Column flexGrow={0.6}>
          <HeaderCell className="fw-bold text-black">
            <span title="Champ's id no.">Champ's id no.</span>
          </HeaderCell>
          <TextCell component="Caregiver" dataKey="champsIdNo" />
        </Column>

        <Column flexGrow={0.5} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Rate">Rate</span>
          </HeaderCell>
          <TextCell component="Caregiver" dataKey="rate" />
        </Column>

        {!isInProfile && (
          <Column align="center" flexGrow={0.7}>
            <HeaderCell className="fw-bold text-black">
              <span
                title="Clock On/Off"
                className="click"
                onClick={() => {
                  setSortBy("autoClockCheckIn");
                  toggleFilterAutoClockCheckIn();
                }}
              >
                Clock On/Off
                <BooleanToggleFilterIcon
                  filterValue={
                    filterAutoClockCheckIn === 0
                      ? "false"
                      : filterAutoClockCheckIn === 1
                      ? "true"
                      : "default"
                  }
                  className="ms-2 toggle-filter-icon"
                />
              </span>
            </HeaderCell>
            <Cell>
              {(rowData: ApiSchemaCaregiver) => (
                <AutoClockToggle
                  value={rowData.autoClockCheckIn}
                  caregiver={rowData}
                />
              )}
            </Cell>
          </Column>
        )}

        {!isInProfile && (
          <Column align="center" flexGrow={0.5}>
            <HeaderCell className="fw-bold text-black">
              <span
                title="GPS"
                className="click"
                onClick={() => {
                  setSortBy("gpsCheckInTurnedOn");
                  toggleFilterGPSCheckIn();
                }}
              >
                GPS
                <BooleanToggleFilterIcon
                  filterValue={
                    filterGPSCheckIn === 0
                      ? "false"
                      : filterGPSCheckIn === 1
                      ? "true"
                      : "default"
                  }
                  className="ms-2 toggle-filter-icon"
                />
              </span>
            </HeaderCell>
            <Cell>
              {(rowData: ApiSchemaCaregiver) => (
                <GPSCheckInToggle caregiver={rowData} />
              )}
            </Cell>
          </Column>
        )}

        {!isInProfile && (
          <Column align="center" flexGrow={0.6}>
            <HeaderCell className="fw-bold text-black">Notification</HeaderCell>
            <Cell>
              {(rowData: ApiSchemaCaregiver) => (
                <NotificationToggle caregiver={rowData} />
              )}
            </Cell>
          </Column>
        )}

        {data?.[0] && !isInProfile && (
          <Column flexGrow={1}>
            <HeaderCell className="fw-bold text-black">
              <span title="Add Patient">Add Patient</span>
            </HeaderCell>
            {/* <SeePatientCell /> */}
            <Cell>
              {(rowData: TableData) => (
                <button
                  type="button"
                  onClick={() =>
                    routerHistory.push(AppRouteUi.Appointment.Add(), {
                      caregiverSKey: rowData.sKey,
                    })
                  }
                  className="btn btn-xs btn-green"
                >
                  {rowData.patientNames?.length ? "Update" : "Add"} Patient
                </button>
              )}
            </Cell>
          </Column>
        )}

        {isInProfile && (
          <Column align="center" flexGrow={1}>
            <HeaderCell className="fw-bold text-black">
              <span title="Auto Clock">Auto Clock</span>
            </HeaderCell>
            <Cell>
              {(rowData: ApiSchemaCaregiver) =>
                rowData.autoClockCheckIn ? "Enabled" : "Disabled"
              }
            </Cell>
          </Column>
        )}

        <Column align="center" flexGrow={1}>
          <HeaderCell className="fw-bold text-black">
            <span title="Action">Action</span>
          </HeaderCell>
          <Cell>
            {(rowData: TableData) => (
              <div className="d-flex align-items-center justify-content-center gap-2">
                <button 
                  className="btn btn-sm btn-outline-info"
                  onClick={() => routerHistory.push(AppRouteUi.Caregiver.ByIdV2(rowData.sKey))}
                  title="View"
                >
                  <FiEye />
                </button>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => routerHistory.push(AppRouteUi.Caregiver.UpdateById(rowData.sKey!))}
                  title="Edit"
                >
                  <FiEdit />
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleSelectMenu('delete', new Event('click'), rowData.sKey)}
                  title="Delete"
                >
                  <FiTrash2 />
                </button>
                <button
                  className="btn btn-sm btn-outline-warning"
                  onClick={() => handleSelectMenu('changeStatus', new Event('click'), rowData.sKey, null, rowData.isDeActivated)}
                  title={rowData.isDeActivated ? 'Activate' : 'Deactivate'}
                >
                  <FiPower />
                </button>
              </div>
            )}
          </Cell>
        </Column>
      </Table>

      {data && (
        <div className="d-flex justify-content-end mt-3">
          <Pagination
            total={filteredTableData.length}
            pages={
              totalCount
                ? Math.ceil(totalCount / pageSize)
                : Math.ceil(filteredTableData.length / pageSize)
            }
            maxButtons={6}
            next={true}
            prev={true}
            activePage={pageNumber}
            onSelect={(page: any) =>
              dispatch(ActionsCaregiverTable.goToPage(page))
            }
          />
        </div>
      )}
    </div>
  );
};
